import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep, upperFirst } from 'lodash-es';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { SitemateTaskTypes, ISitemateTaskDefinitionUrl } from '@site-mate/dashpivot-shared-library';
import { CompanyPlanTypes } from '@site-mate/sitemate-global-shared';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ISitemateTaskDefinitionWeb } from 'app/shared/model/task-definition.model';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { AccountUpgradeLimitModalContext } from 'app/user/account-upgrade/account-upgrade-limit/account-upgrade-limit-context.enum';
import { AccountUpgradeLimitService } from 'app/user/account-upgrade/account-upgrade-limit/account-upgrade-limit.service';
import { UserService } from 'app/user/user.service';

import { LinkCreateStatus } from '../../../shared/model/sitemate/link-status.enum';
import { TemplateLinksModalComponent } from '../template-links-modal/template-links-modal.component';

@Component({
  selector: 'cc-template-links',
  templateUrl: './template-links.component.html',
  styleUrls: ['./template-links.component.scss'],
})
export class TemplateLinksComponent implements OnInit {
  readonly companyPlanTypes = CompanyPlanTypes;
  readonly sitemateTaskTypes = SitemateTaskTypes;

  links: ISitemateTaskDefinitionUrl[] = [];
  isLoading = true;
  isTeamControllerOrAbove = false;
  titles: Record<SitemateTaskTypes, string>;
  subtitles: Record<SitemateTaskTypes, string>;
  articleLinks: Record<SitemateTaskTypes, string>;
  modalReference: NgbModalRef | undefined;
  taskType: SitemateTaskTypes;
  hierarchy = AppHierarchy.Team;
  itHasTemplates: boolean;
  teamId: string;
  companyPlanType: CompanyPlanTypes;
  upgradePlanMessage: string;
  canCreateLink: boolean;
  bannerText: string;
  isBannerVisible: boolean;
  legacyRestrictions: boolean;

  private unSubscribe = new Subject<void>();

  constructor(
    private readonly routeParamsService: RouteParamsService,
    private readonly route: ActivatedRoute,
    private readonly userService: UserService,
    private readonly i18nService: TmpI18NService,
    private readonly modal: NgbModal,
    private readonly accountUpgradeLimitService: AccountUpgradeLimitService,
  ) {}

  ngOnInit(): void {
    this.populateTextFields();
    this.taskType = this.route.snapshot.data.taskType;
    this.teamId = this.route.snapshot.paramMap.get('teamId');
    this.upgradePlanMessage = this.i18nService.getMessage('upgradePlanToUnlockFeature');
    this.loadTemplatesAndLinks();
  }

  private populateTextFields(): void {
    this.titles = {
      [SitemateTaskTypes.FormSingle]: this.i18nService.getMessage('templateLinksSingleTitle'),
      [SitemateTaskTypes.FormRecurring]: this.i18nService.getMessage('templateLinksRecurringTitle'),
    };
    this.subtitles = {
      [SitemateTaskTypes.FormSingle]: this.i18nService.getMessage('templateLinksSingleSubTitle'),
      [SitemateTaskTypes.FormRecurring]: this.i18nService.getMessage('templateLinksRecurringSubTitle'),
    };
    this.articleLinks = {
      [SitemateTaskTypes.FormSingle]:
        'https://intercom.help/dashpivot/en/articles/5252492-single-submissions',
      [SitemateTaskTypes.FormRecurring]: 'https://intercom.help/dashpivot/en/articles/5252487-ongoing-access',
    };
  }

  openCreateLinkModal(): void {
    const modalRef = this.openModal();
    modalRef.onLinkCreated.subscribe((links: ISitemateTaskDefinitionWeb) => {
      this.links = [links, ...this.links];
      this.routeParamsService.set(this.taskType, this.links);
    });
  }

  onEditLink(taskDefinition: ISitemateTaskDefinitionWeb): void {
    const modalRef = this.openModal();
    modalRef.status = LinkCreateStatus.Edit;

    modalRef.taskDefinition = cloneDeep(taskDefinition);
    modalRef.onLinkCreated.subscribe(this.changeLinkData.bind(this));
  }

  onUnarchiveLink(taskDefinition: ISitemateTaskDefinitionWeb) {
    const modalRef = this.openModal();
    modalRef.status = LinkCreateStatus.Unarchive;

    modalRef.taskDefinition = cloneDeep(taskDefinition);
    modalRef.onLinkCreated.subscribe(this.changeLinkData.bind(this));
  }

  private updateLocalLinksWithEditedLink(link: ISitemateTaskDefinitionWeb): ISitemateTaskDefinitionUrl[] {
    const localLinksClone = [...this.links];
    const index = localLinksClone.findIndex((entry) => entry.url === link.url);
    localLinksClone[index] = link;
    return localLinksClone;
  }

  onChangeLinkData(taskDefinition: ISitemateTaskDefinitionWeb): void {
    this.changeLinkData(taskDefinition);
  }

  openModal(): TemplateLinksModalComponent {
    const modalRef = this.modal.open(TemplateLinksModalComponent, {
      size: 'lg',
      windowClass: 'templates-link-modal d-flex align-items-center',
    });
    modalRef.componentInstance.teamId = this.teamId;
    modalRef.componentInstance.type = this.taskType;

    return modalRef.componentInstance as TemplateLinksModalComponent;
  }

  private changeLinkData(link: ISitemateTaskDefinitionWeb): void {
    this.links = this.updateLocalLinksWithEditedLink(link);
    this.routeParamsService.set(this.taskType, this.links);
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  loadTemplatesAndLinks(): void {
    combineLatest([
      this.routeParamsService.get('templates').pipe(takeUntil(this.unSubscribe)),
      this.routeParamsService.get('companyFromTeam').pipe(takeUntil(this.unSubscribe)),
      this.routeParamsService.get(this.taskType).pipe(takeUntil(this.unSubscribe)),
    ])
      .pipe(
        takeUntil(this.unSubscribe),
        filter(([templates, company, links]) => !!templates && !!company && !!links),
      )
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .subscribe(([templates, company, links]) => {
        this.links = links;
        this.itHasTemplates = templates && templates.length > 0;
        this.isTeamControllerOrAbove = !this.userService.isTeamControllerOrAbove();
        this.companyPlanType = company.billing.planType;
        this.legacyRestrictions = company.billing?.legacyRestrictions;
        this.isLoading = false;

        this.setLinkCreationPermission();
        this.setBannerConfiguration();
      });
  }

  private setLinkCreationPermission() {
    const isNotStandardPlan = this.companyPlanType !== this.companyPlanTypes.Standard;
    const isFormSingleTask = this.taskType === this.sitemateTaskTypes.FormSingle;
    const isLegacyPlanWithRestrictions =
      this.companyPlanType === this.companyPlanTypes.Legacy && this.legacyRestrictions;

    this.canCreateLink =
      (this.itHasTemplates && isNotStandardPlan && !isLegacyPlanWithRestrictions) || isFormSingleTask;
  }

  private setBannerConfiguration() {
    const isStandardPlan = this.companyPlanType === this.companyPlanTypes.Standard;
    const isLegacyPlan = this.companyPlanType === this.companyPlanTypes.Legacy;
    const isFormRecurringTask = this.taskType === this.sitemateTaskTypes.FormRecurring;

    this.isBannerVisible = isFormRecurringTask && (isLegacyPlan ? this.legacyRestrictions : isStandardPlan);

    this.bannerText = `Sitemate Paid Users are not included on the ${upperFirst(this.companyPlanType)} Plan:`;
  }

  openUpgradeModal() {
    this.accountUpgradeLimitService.openModal(AccountUpgradeLimitModalContext.SitematePaidUsers);
  }
}
